import { BooliResponse } from './api/httpHandlers'

const mode = process.env.NODE_ENV

export const appSteps = [
  'start',
  'postal_code',
  'construction_year',
  'living_area',
  'solar_panel_power',
  'heat_source',
  'result',
] as const

export type StepName = (typeof appSteps)[number]

export function getStepIndex(stepName: StepName): number {
  return appSteps.indexOf(stepName)
}

export interface UserData {
  postal_code: string
  construction_year: string
  living_area: string
  solar_panel_power: string
  heat_source: string[]
}

export interface AppState {
  step: (typeof appSteps)[number]
  userData: UserData
  isFetching: boolean
  isValidatingPostalCode: boolean
  data?: BooliResponse
  hasAppError: boolean
}

export type AppAction =
  | { type: 'goBack' }
  | { type: 'goForward' }
  | {
      type: 'inputValueChange'
      value: string | string[]
    }
  | {
      type: 'fetchingDone'
      data: BooliResponse
    }
  | {
      type: 'setIsValidatingPostalCode'
      value: boolean
    }
  | {
      type: 'setHasAppError'
      value: boolean
    }
  | { type: 'reset' }

function getCurrentStepIndex(currentStep: AppState['step']): number {
  return appSteps.indexOf(currentStep)
}

function getNextStep(currentStep: AppState['step']): AppState['step'] {
  const currentIndex = getCurrentStepIndex(currentStep)
  const nextIndex = Math.min(currentIndex + 1, appSteps.length - 1)
  return appSteps[nextIndex]
}

function getPreviousStep(currentStep: AppState['step']): AppState['step'] {
  const currentIndex = getCurrentStepIndex(currentStep)
  const prevIndex = Math.max(currentIndex - 1, 0)
  return appSteps[prevIndex]
}

export function appReducer(state: AppState, action: AppAction): AppState {
  if (mode !== 'production') console.log('appReducerAction: ', action)
  switch (action.type) {
    case 'goBack': {
      const prevStep = getPreviousStep(state.step)
      return { ...state, step: prevStep, isFetching: false }
    }

    case 'goForward': {
      const nextStep = getNextStep(state.step)
      if (nextStep === 'result') {
        return { ...state, isFetching: true }
      } else {
        return { ...state, step: nextStep }
      }
    }

    case 'inputValueChange': {
      const defaultUserData = {
        postal_code: '',
        construction_year: '',
        living_area: '',
        solar_panel_power: null,
        heat_source: null,
      }
      const userData = {
        ...defaultUserData,
        ...state.userData,
        [state.step]: action.value,
      }
      return { ...state, userData }
    }

    case 'fetchingDone': {
      const step = getNextStep(state.step)
      return { ...state, isFetching: false, data: action.data, step }
    }

    case 'setIsValidatingPostalCode': {
      return { ...state, isValidatingPostalCode: action.value }
    }

    case 'setHasAppError': {
      return { ...state, hasAppError: action.value }
    }

    default: {
      if (mode !== 'production') console.error('Unknown action type', action)
      return { ...state, hasAppError: true }
    }
  }
}

export const initalState: AppState = {
  step: 'start',
  userData: {
    postal_code: '',
    construction_year: '',
    living_area: '',
    solar_panel_power: '',
    heat_source: [],
  },
  isFetching: false,
  isValidatingPostalCode: false,
  hasAppError: false,
}
